// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.6.0 — where a federated sign-in lands (D8).
 *
 * ## Why this page exists at all
 *
 * The session cookies are `SameSite=Strict` and an identity provider's POST to
 * the assertion consumer is cross-site by definition, so a cookie set on that
 * response is a cookie the browser will never send back. The consumer
 * therefore redirects HERE — same site — with a one-time code, and this page
 * exchanges it over a same-site POST that sets the cookie pair through the same
 * code the password login route uses.
 *
 * ## It is deliberately almost empty
 *
 * Nobody should read it. It exists for the fraction of a second between an
 * identity provider's redirect and the application, and the only thing worth
 * putting on it is a sentence saying so and a way out when the exchange fails.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api-client.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useLocale } from '../hooks/useLocale.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { mfaStepUpPath } from '../utils/mfa-step-up.js';
import { safeNextPath } from '../utils/safe-next-path.js';

export function SsoHandoffPage() {
  const { t } = useLocale();
  usePageTitle('Single sign-on');
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [failed, setFailed] = useState(false);

  /*
   * The code is single-use at the server, so a second exchange would be
   * refused and would show this page's failure state to somebody who had
   * actually signed in successfully. React 18's development StrictMode mounts
   * effects twice, which is exactly that second exchange.
   */
  const exchanged = useRef(false);

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) {
      setFailed(true);
      return;
    }
    if (exchanged.current) return;
    exchanged.current = true;

    let cancelled = false;
    apiClient
      .exchangeSsoHandoff(code)
      .then((result) => {
        if (cancelled) return;
        /*
         * `redirect_to` is the destination stored with the login when it
         * STARTED, validated then. Nothing in this URL decides where the
         * browser goes next; the query string carries a code and nothing else.
         */
        const destination = result.redirect_to || '/admin';

        /*
         * Gate E12.5 pass 2 (P2) — the session exists (the exchange set the
         * cookies) but it may not be USABLE yet, and the old code ignored both
         * flags the exchange returns, sending a held session to a destination
         * where every request failed:
         *
         *  - `mfa_required`: the person holds an authenticator and the provider's
         *    own factor did not stand in for it (the organisation does not accept
         *    it, or the claims did not match). They enter a code on the platform's
         *    own prompt, then continue to where they were going.
         *  - `mfa_enrolment_required`: the organisation requires a second factor
         *    and this account has none. They enrol on the interstitial.
         *
         * `LoginPage` does the same for a password sign-in. The step-up is
         * checked first, as there: the challenge is the narrower remedy.
         */
        const next = safeNextPath(destination);
        if (result.mfa_required) {
          login({ ...result.user, mfa_required: true });
          navigate(mfaStepUpPath(next), { replace: true });
          return;
        }
        if (result.mfa_enrolment_required) {
          login({ ...result.user, mfa_enrolment_required: true });
          navigate(
            next
              ? `/mfa-enrolment-required?next=${encodeURIComponent(next)}`
              : '/mfa-enrolment-required',
            { replace: true },
          );
          return;
        }
        login(result.user);
        navigate(destination, { replace: true });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, login, navigate]);

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>{t.login.title}</h1>
        {failed ? (
          <>
            <div className="login-error" role="alert">
              {t.login.sso_failed}
            </div>
            <div className="login-links" style={{ textAlign: 'center' }}>
              <a
                href="/login"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/login', { replace: true });
                }}
              >
                {t.login.sign_in}
              </a>
            </div>
          </>
        ) : (
          <p className="login-subtitle" role="status">
            {t.login.sso_completing}
          </p>
        )}
      </div>
    </div>
  );
}

export default SsoHandoffPage;
